import { ReactNode, useRef } from "react";

import { UseFormReturn } from "react-hook-form";

import { ScrollContext } from "../AnimatePreview/getScrollTopStepTarget";
import { useAnimatePreviewOnFormEvents } from "../AnimatePreview/useAnimatePreviewOnFormEvents";
import { LiveFormActionFooterPortal, StepActionFooterProps } from "../common/LiveFormEditorActionFooter";
import { useLiveFormEditorContext } from "../common/LiveFormEditorContext";

import { AnyStepFormData } from "./AnyStepFormData";

import { useWarnIfUnsavedChanges } from "@/common/hooks/useWarnIfUnsavedChanges";
import { StepContainer } from "@/components/LiveEditorElements/StepContainer/StepContainer";

interface FormStepContainerProps<T extends AnyStepFormData> {
  children: ReactNode;
  actionsProps: StepActionFooterProps;
  title?: string;
  titleTooltip?: string;
  description?: string | ReactNode;
  form: UseFormReturn<T>;
  "data-test"?: string;
  /** Set to false to disable the default animation hook (for steps with custom animation) */
  animatePreview?: boolean;
  /** Optional callback to provide scroll context for dynamic scroll targets */
  getScrollContext?: () => ScrollContext;
}

export const FormStepContainer = <T extends AnyStepFormData>({
  children,
  actionsProps,
  title,
  titleTooltip,
  description,
  form,
  "data-test": dataTest,
  animatePreview = true,
  getScrollContext,
}: FormStepContainerProps<T>) => {
  const { isStepChanging } = useLiveFormEditorContext();
  const formContainerRef = useRef<HTMLDivElement>(null);

  if (animatePreview) {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- animatePreview is static per component, safe to conditionally call
    useAnimatePreviewOnFormEvents<T>(formContainerRef, form, getScrollContext);
  }

  // Ask confirmation if the user is trying to leave the page without saving
  const { isDirty } = form.formState;
  useWarnIfUnsavedChanges(isDirty);

  return (
    <StepContainer
      title={title}
      titleTooltip={titleTooltip}
      description={description}
      isStepChanging={isStepChanging}
      formContainerRef={formContainerRef}
      slots={{
        actionFooter: <LiveFormActionFooterPortal {...actionsProps} />,
      }}
      data-test={dataTest}
    >
      {children}
    </StepContainer>
  );
};
